import type { ParseKeys } from 'i18next';
import type { MiceTravelerCostCenterSource } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import {
  useCostCenterChoices,
  useSetTravelerCostCenter,
  useTravelerCostCenters,
} from '../hooks/use_traveler_cost_centers';
import { RefSelect } from './new_event_steps';
import { DrawerSection } from './need_recommendation';

const SOURCE = {
  sheet: 'mice.costCenter.source.sheet',
  operator: 'mice.costCenter.source.operator',
  premises: 'mice.costCenter.source.premises',
  traveler: 'mice.costCenter.source.traveler',
} as const satisfies Record<MiceTravelerCostCenterSource, ParseKeys>;

interface CostCenterSectionProps {
  projectId: string;
  travelerEmail: string;
  /** `mice:manage`, a permissão da rota que grava: trocar; sem ela, só o valor. */
  canChange: boolean;
}

/** O centro de custo do viajante e de onde veio; o operador troca enquanto a compra não começou. */
export function CostCenterSection(props: Readonly<CostCenterSectionProps>) {
  const { projectId, travelerEmail } = props;
  const data = useTravelerCostCenters(projectId).data;
  const locked = data?.locked.includes(travelerEmail) ?? false;
  const editable = props.canChange && !locked;
  const choices = useCostCenterChoices(projectId, editable);
  const change = useSetTravelerCostCenter(projectId);
  if (!data) return null;
  const own = data.items.find((i) => i.email === travelerEmail);
  const current = own?.costCenter ?? data.premises;
  const pathText =
    data.premisesPath === 'event'
      ? t('mice.corporatePath.undefined')
      : t(`mice.corporatePath.costCenter.${data.premisesPath}`);

  return (
    <DrawerSection title={t('mice.costCenter.title')}>
      <div className="flex flex-col gap-1">
        <span className="text-body text-foreground">{current?.name ?? pathText}</span>
        {/* Sem centro de custo, o caminho já diz de onde ele vem: a origem fica de fora. */}
        {current ? (
          <span className="text-meta text-muted-foreground">
            {t(SOURCE[own?.source ?? 'premises'])}
          </span>
        ) : null}
      </div>
      {locked ? (
        <p className="text-meta text-muted-foreground">{t('mice.costCenter.locked')}</p>
      ) : null}
      {editable ? (
        <RefSelect
          id="mice-traveler-cc"
          label={t('mice.costCenter.title')}
          items={choices}
          value={current}
          onChange={(next) => {
            if (next && next.id !== current?.id) {
              change.mutate({ email: travelerEmail, costCenterId: next.id });
            }
          }}
        />
      ) : null}
    </DrawerSection>
  );
}
